import React, { useEffect, useMemo, useState } from 'react';
import { useLocaleState, useTranslate } from 'react-admin';
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { fetchFocusTimesheet } from '../../services/api';
import Pill from '../reviews/Pill';

const WEEK_OPTIONS = [4, 6, 12];
// Weekdays take the room, the weekend stays narrow.
const COLUMNS = 'repeat(5, minmax(0, 1fr)) repeat(2, minmax(0, 0.45fr))';

const hatch = (color) => `repeating-linear-gradient(135deg, ${color} 0 6px, transparent 6px 12px)`;

const DayTooltip = ({ day, translate }) => (
    <Box sx={{ maxWidth: 320 }}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>{day.date} · {day.hours} h</Typography>
        {day.entries.map(entry => (
            <Typography key={entry.id} variant="caption" component="p">
                {entry.hours} h · {entry.project} · {entry.description || translate('(no description)')}
            </Typography>
        ))}
    </Box>
);

const DayCell = ({ day, colors, selected, onSelect, maxHours, locale }) => {
    const translate = useTranslate();
    const theme = useTheme();
    const dayNumber = new Date(`${day.date}T12:00:00`).toLocaleDateString(locale, { day: 'numeric', month: 'short' });

    let background = 'transparent';
    let border = `1px solid ${theme.palette.divider}`;
    let label = null;
    if (day.future) {
        border = `1px dashed ${alpha(theme.palette.text.primary, 0.08)}`;
    } else if (day.holiday) {
        background = hatch(alpha(theme.palette.text.primary, 0.06));
        label = translate('Holiday');
    } else if (day.leave >= 1) {
        background = hatch(alpha(theme.palette.info.main, 0.12));
        label = translate('Leave');
    } else if (day.missing) {
        border = `1px dashed ${theme.palette.error.main}`;
        background = alpha(theme.palette.error.main, 0.04);
        label = translate('nothing');
    } else if (day.weekend) {
        background = alpha(theme.palette.text.primary, 0.03);
    }
    if (selected) {
        border = `2px solid ${theme.palette.primary.main}`;
    }

    const cell = (
        <Box
            onClick={() => day.entries.length && onSelect(day.date)}
            sx={{
                minHeight: 62, borderRadius: '8px', border, background, padding: '6px', display: 'flex', flexDirection: 'column',
                gap: '4px', cursor: day.entries.length ? 'pointer' : 'default', overflow: 'hidden',
                '&:hover': day.entries.length ? { boxShadow: `0 0 0 1px ${theme.palette.primary.main}` } : {},
            }}
        >
            <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={0.5}>
                <Typography variant="caption" color="text.secondary" noWrap>{dayNumber}</Typography>
                {day.hours > 0 && <Typography variant="body2" sx={{ fontWeight: 600 }}>{day.hours} h</Typography>}
            </Box>
            {day.hours > 0 && (
                <Box sx={{ display: 'flex', height: 8, borderRadius: '4px', overflow: 'hidden', width: `${Math.min(100, 100 * day.hours / maxHours)}%` }}>
                    {day.projects.map(project => (
                        <Box key={project.project_id ?? 'none'} sx={{ flexGrow: project.hours, backgroundColor: colors[project.project_id ?? 0] }} />
                    ))}
                </Box>
            )}
            {label && <Typography variant="caption" color={day.missing ? 'error.main' : 'text.secondary'}>{label}</Typography>}
            {day.leave > 0 && day.leave < 1 && <Typography variant="caption" color="text.secondary">{translate('Half-day leave')}</Typography>}
        </Box>
    );

    return day.entries.length ? <Tooltip title={<DayTooltip day={day} translate={translate} />}>{cell}</Tooltip> : cell;
};

const DayDetail = ({ day, colors, locale }) => {
    const translate = useTranslate();
    if (!day) {
        return null;
    }
    const date = new Date(`${day.date}T12:00:00`).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
    const title = date.charAt(0).toUpperCase() + date.slice(1);
    return (
        <Box sx={{ marginTop: '16px' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title} · {day.hours} h</Typography>
            {day.entries.map(entry => (
                <Box key={entry.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', marginTop: '6px', flexShrink: 0, backgroundColor: colors[entry.project_id ?? 0] }} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{entry.project}</Typography>
                            {entry.type && <Typography variant="caption" color="text.secondary">{entry.type}</Typography>}
                            {entry.vague && <Pill label={translate('vague')} color="warning" variant="outlined" />}
                            {entry.late_days > 1 && <Pill label={translate('logged %{days} d later', { days: entry.late_days })} color="error" variant="outlined" />}
                        </Box>
                        <Typography variant="body2" color={entry.description ? 'text.primary' : 'text.disabled'} sx={{ whiteSpace: 'pre-line' }}>
                            {entry.description || translate('(no description)')}
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{entry.hours} h</Typography>
                </Box>
            ))}
        </Box>
    );
};

// What the person did, day by day, over the last weeks. Read only.
const TimesheetCalendar = ({ userId }) => {
    const translate = useTranslate();
    const [locale] = useLocaleState();
    const [weeks, setWeeks] = useState(6);
    const [data, setData] = useState(null);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        let active = true;
        fetchFocusTimesheet(userId, weeks).then(result => {
            if (!active) return;
            setData(result);
            const lastFilled = [...result.days].reverse().find(day => day.entries.length);
            setSelected(lastFilled ? lastFilled.date : null);
        }).catch(() => active && setData({ days: [], projects: [] }));
        return () => {
            active = false;
        };
    }, [userId, weeks]);

    const colors = useMemo(
        () => Object.fromEntries((data?.projects || []).map(project => [project.project_id ?? 0, project.color])),
        [data],
    );

    if (!data) {
        return null;
    }

    const rows = [];
    for (let index = 0; index < data.days.length; index += 7) {
        rows.push(data.days.slice(index, index + 7));
    }
    rows.reverse();
    const maxHours = Math.max(8, ...data.days.map(day => day.hours));
    const missing = data.days.filter(day => day.missing).length;
    const selectedDay = data.days.find(day => day.date === selected);
    const weekdayNames = [1, 2, 3, 4, 5, 6, 7].map(offset =>
        new Date(2024, 0, offset).toLocaleDateString(locale, { weekday: 'short' }));

    return (
        <Card variant="outlined" sx={{ borderRadius: '12px', marginBottom: '16px' }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ marginBottom: '8px' }}>
                    <Box>
                        <Typography variant="h6">{translate('Timesheet')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {data.start} → {data.end}
                            {missing ? ` · ${missing} ${translate('days due without any line')}` : ''}
                        </Typography>
                    </Box>
                    <ToggleButtonGroup exclusive size="small" value={weeks} onChange={(event, value) => value && setWeeks(value)}>
                        {WEEK_OPTIONS.map(option => (
                            <ToggleButton key={option} value={option} sx={{ textTransform: 'none', paddingX: 1.5 }}>
                                {option} {translate('weeks')}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                <Box display="flex" gap={1.5} flexWrap="wrap" sx={{ marginBottom: '12px' }}>
                    {data.projects.map(project => (
                        <Box key={project.project_id ?? 'none'} display="flex" alignItems="center" gap={0.75}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: project.color }} />
                            <Typography variant="caption">{project.name} · {project.hours} h</Typography>
                        </Box>
                    ))}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: COLUMNS, gap: 0.75, marginBottom: '4px' }}>
                    {weekdayNames.map(name => (
                        <Typography key={name} variant="caption" color="text.secondary" sx={{ textAlign: 'center', textTransform: 'capitalize' }}>{name}</Typography>
                    ))}
                </Box>
                <Box sx={{ display: 'grid', gap: 0.75 }}>
                    {rows.map(week => (
                        <Box key={week[0].date} sx={{ display: 'grid', gridTemplateColumns: COLUMNS, gap: 0.75 }}>
                            {week.map(day => (
                                <DayCell key={day.date} day={day} colors={colors} maxHours={maxHours} locale={locale}
                                    selected={day.date === selected} onSelect={setSelected} />
                            ))}
                        </Box>
                    ))}
                </Box>

                <DayDetail day={selectedDay} colors={colors} locale={locale} />
            </CardContent>
        </Card>
    );
};

export default TimesheetCalendar;
